import { useState, useEffect, useCallback } from 'react';
import { getMemorySnapshots, getMemoryStats, getMemorySnapshot } from '../services/aiOperationsApi';
import { useProject } from '../contexts/ProjectContext';

interface MemoryFilters {
  memoryType: string;
  operation: string;
  runId: string;
  days: number;
  [key: string]: string | number;
}

interface TimelineItem {
  id: string;
  name: string;
  type: string;
  operation: string | undefined;
  start: number;
  duration: number;
  end: number;
  depth: number;
  metadata: {
    token_count: number | undefined;
    max_tokens: number | undefined;
    utilization: string | null;
    run_id: string | undefined;
    timestamp: string | undefined;
    content_json: unknown;
  };
}

/**
 * Custom hook for fetching and managing memory data
 */
export const useMemoryData = (initialFilters: Partial<MemoryFilters> = {}) => {
  const { selectedProjectId } = useProject();
  const [snapshots, setSnapshots] = useState<Record<string, any>[]>([]);
  const [stats, setStats] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [filters, setFilters] = useState<MemoryFilters>({
    memoryType: '',
    operation: '',
    runId: '',
    days: 7,
    ...initialFilters
  });

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // Build params for snapshots (exclude days)
      const snapshotsParams: Record<string, any> = {
        page: currentPage,
        limit: 50,
        ...(filters.memoryType && { memoryType: filters.memoryType }),
        ...(filters.operation && { operation: filters.operation }),
        ...(filters.runId && { runId: filters.runId }),
      };

      // Fetch snapshots and stats in parallel
      const [snapshotsData, statsData] = await Promise.all([
        getMemorySnapshots(snapshotsParams as any),
        getMemoryStats({ days: filters.days, runId: filters.runId } as any)
      ]);

      setSnapshots(snapshotsData.data || snapshotsData.snapshots || []);
      setTotalPages(snapshotsData.meta?.totalPages || 1);
      setStats(statsData);
    } catch (err: unknown) {
      console.error('Error loading memory data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, selectedProjectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateFilters = useCallback((newFilters: Partial<MemoryFilters>): void => {
    setFilters(prev => ({ ...prev, ...newFilters } as MemoryFilters));
    setCurrentPage(1); // Reset to first page on filter change
  }, []);

  const clearFilters = useCallback((): void => {
    setFilters({
      memoryType: '',
      operation: '',
      runId: '',
      days: 7,
    });
    setCurrentPage(1);
  }, []);

  const goToPage = useCallback((page: number): void => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  }, [totalPages]);

  const refresh = useCallback((): void => {
    loadData();
  }, [loadData]);

  return {
    snapshots,
    stats,
    loading,
    error,
    currentPage,
    totalPages,
    filters,
    updateFilters,
    clearFilters,
    goToPage,
    refresh,
  };
};

/**
 * Custom hook for fetching a single memory snapshot
 */
export const useMemorySnapshot = (snapshotId: string | null | undefined) => {
  const [snapshot, setSnapshot] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!snapshotId) {
      setSnapshot(null);
      return;
    }

    const loadSnapshot = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMemorySnapshot(snapshotId);
        setSnapshot(data);
      } catch (err: unknown) {
        console.error('Error loading memory snapshot:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadSnapshot();
  }, [snapshotId]);

  return { snapshot, loading, error };
};

/**
 * Transform memory snapshots for timeline visualization
 */
export const transformSnapshotsToTimeline = (snapshots: Record<string, any>[]): TimelineItem[] => {
  if (!snapshots || snapshots.length === 0) return [];

  // Sort by timestamp
  const sorted = [...snapshots].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const baseTime = new Date(sorted[0].timestamp).getTime();

  return sorted.map((snapshot, index) => {
    const timestamp = new Date(snapshot.timestamp).getTime();
    const start = timestamp - baseTime;

    return {
      id: snapshot.id || `snapshot-${index}`,
      name: `${snapshot.memory_type || 'memory'} - ${snapshot.operation || 'operation'}`,
      type: snapshot.memory_type || 'other',
      operation: snapshot.operation,
      start,
      duration: 100, // Visual duration for display
      end: start + 100,
      depth: 0,
      metadata: {
        token_count: snapshot.token_count,
        max_tokens: snapshot.max_tokens,
        utilization: snapshot.max_tokens
          ? ((snapshot.token_count / snapshot.max_tokens) * 100).toFixed(1)
          : null,
        run_id: snapshot.run_id,
        timestamp: snapshot.timestamp,
        content_json: snapshot.content_json,
      },
    };
  });
};

export default useMemoryData;
